import { useEffect, useState, useCallback } from 'react';
import { ContactPhones } from './ContactPhones';
import { ContactEmails } from './ContactEmails';
import { ContactAddresses } from './ContactAddresses';
import { ContactWebDomains } from './ContactWebDomains';
import { ContactBasicInfo } from './ContactBasicInfo';
import { ContactCustomFields } from './ContactCustomFields';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';

interface ContactDetailsTabProps {
  contactId: string;
  onUpdate?: () => void;
}

export function ContactDetailsTab({ contactId, onUpdate }: ContactDetailsTabProps) {
  const [contact, setContact] = useState<any>(null);
  const [phones, setPhones] = useState<any[]>([]);
  const [emails, setEmails] = useState<any[]>([]);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [webDomains, setWebDomains] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchDetails = useCallback(async () => {
    if (!contactId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch contact basic info
      const { data: contactData, error: contactError } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', contactId)
        .single();

      if (contactError) throw contactError;
      setContact(contactData);

      // Use Promise.all to fetch all related data in parallel
      const [phonesRes, emailsRes, addressesRes, webDomainsRes] = await Promise.all([
        supabase
          .from('phone_numbers')
          .select('*')
          .eq('contact_id', contactId)
          .order('isPrimary', { ascending: false }),
        supabase
          .from('email_address')
          .select('*')
          .eq('contact_id', contactId)
          .order('is_primary', { ascending: false }),
        supabase
          .from('physical_addresses')
          .select('*')
          .eq('contact_id', contactId)
          .order('is_primary', { ascending: false }),
        supabase
          .from('website_domains')
          .select('*')
          .eq('contact_id', contactId)
          .order('is_primary', { ascending: false }),
      ]);

      if (phonesRes.error) throw phonesRes.error;
      if (emailsRes.error) throw emailsRes.error;
      if (addressesRes.error) throw addressesRes.error;
      if (webDomainsRes.error) throw webDomainsRes.error;

      setPhones(phonesRes.data);
      setEmails(emailsRes.data);
      setAddresses(addressesRes.data);
      setWebDomains(webDomainsRes.data);
    } catch (error) {
      console.error('Error fetching contact details:', error);
      setError('Failed to load contact details');
      captureError(error as Error, { feature: 'contact-details-tab-save' });
      toast({
        title: "Error",
        description: "Failed to load contact details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [contactId, toast]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleUpdate = useCallback(() => {
    fetchDetails();
    onUpdate?.();
  }, [fetchDetails, onUpdate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return <div className="text-destructive">{error}</div>;
  }

  if (!contact) {
    return <div>Contact not found</div>;
  }

  return (
    <div className="space-y-4">
      {/* Basic Info */}
      <section className="space-y-4">
        <h3 className="font-semibold text-sm">Basic Information</h3>
        <ContactBasicInfo 
          contact={contact}
          onUpdate={handleUpdate}
        />
      </section>

      {/* Phone Numbers */}
      <section className="space-y-4">
        <div>
          <ContactPhones
            contactId={contactId}
            contactName={`${contact?.first_name || ''} ${contact?.last_name || ''}`.trim()}
            phones={phones}
            onUpdate={handleUpdate}
          />
        </div>
      </section>

      {/* Email Addresses */}
      <section className="space-y-4">
        <div>
          <ContactEmails
            contactId={contactId}
            emails={emails}
            onUpdate={handleUpdate}
          />
        </div>
      </section>

      {/* Web Domains */}
      <section className="space-y-4">
        <div>
          <ContactWebDomains
            contactId={contactId}
            webDomains={webDomains}
            onUpdate={handleUpdate}
          />
        </div>
      </section>

      {/* Addresses */}
      <section className="space-y-4">
        <div>
          <ContactAddresses
            contactId={contactId}
            addresses={addresses}
            onUpdate={handleUpdate}
          />
        </div>
      </section>

      {/* Custom Fields */}
      <section className="space-y-4">
        <div>
          <ContactCustomFields
            contactId={contactId}
            workspaceId={contact.workspace_id}
          />
        </div>
      </section>
    </div>
  );
}